import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { brl, fmtDateTime } from "@/lib/format";
import { can } from "@/lib/permissions";
import { formatAppointmentServices } from "@/lib/appointment";
import { FileText, Loader2, Receipt, ExternalLink, Copy, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientInvoiceDialog({ client, open, onClose, onDone }) {
  const [appts, setAppts] = useState([]);
  const [selected, setSelected] = useState({});
  const [billingType, setBillingType] = useState("PIX");
  const [dueDate, setDueDate] = useState("");
  const [discountType, setDiscountType] = useState("fixed");
  const [discountValue, setDiscountValue] = useState("0");
  const [surcharge, setSurcharge] = useState("0");
  const [descOverride, setDescOverride] = useState("");
  const [issueInvoice, setIssueInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    if (!open || !client) return;
    setInvoice(null); setSelected({}); setDiscountValue("0"); setSurcharge("0"); setDescOverride("");
    const due = new Date(); due.setDate(due.getDate() + 3);
    setDueDate(due.toISOString().slice(0, 10));
    setLoading(true);
    api.get(`/clients/${client.id}/billable-appointments`).then(({ data }) => {
      setAppts(data);
      const sel = {}; data.forEach((a) => { sel[a.id] = true; });
      setSelected(sel);
    }).finally(() => setLoading(false));
  }, [open, client]);

  const subtotal = useMemo(() => appts.filter((a) => selected[a.id]).reduce((s, a) => s + Number(a.price || 0), 0), [appts, selected]);
  const discountAmount = useMemo(() => {
    const v = Number(discountValue) || 0;
    if (!v) return 0;
    return discountType === "percentage" ? subtotal * (v / 100) : v;
  }, [discountType, discountValue, subtotal]);
  const surchargeAmount = Number(surcharge) || 0;
  const total = Math.max(0, subtotal - discountAmount + surchargeAmount);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const toggleAll = (v) => {
    const s = {}; appts.forEach((a) => { s[a.id] = v; }); setSelected(s);
  };

  const generate = async () => {
    if (selectedIds.length === 0) { toast.error("Selecione pelo menos um serviço"); return; }
    if (total <= 0) { toast.error("Valor total precisa ser maior que zero"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/invoices", {
        client_id: client.id,
        appointment_ids: selectedIds,
        billing_type: billingType,
        due_date: dueDate,
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        surcharge_value: Number(surcharge) || 0,
        description_override: descOverride || null,
        issue_invoice: !!issueInvoice,
      });
      setInvoice(data);
      if (data?.nfe) {
        if (data.nfe.issued) toast.success("NF-e agendada no Asaas");
        else toast.warning(data.nfe.detail || "NF-e não emitida — verifique no Asaas");
      }
      toast.success("Cobrança consolidada gerada!");
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.provider_error?.errors?.[0]?.description || "Falha ao gerar cobrança");
    } finally { setCreating(false); }
  };

  const reportUrl = invoice ? `${API}/invoices/${invoice.id}/report` : "";
  const copyReport = () => { navigator.clipboard.writeText(reportUrl); toast.success("Link copiado"); };
  const shareWhats = () => {
    const phone = (client?.phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(
      `Olá ${client.name}! Segue a fatura dos serviços realizados:\n\n` +
      `💰 Total: ${brl(invoice.total)}\n` +
      `📄 Relatório: ${reportUrl}\n` +
      (invoice.invoice_url ? `💳 Pagar: ${invoice.invoice_url}\n` : "")
    );
    const url = phone.length >= 10 ? `https://wa.me/55${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Cobrança consolidada — {client?.name}</DialogTitle>
        </DialogHeader>

        {invoice ? (
          <div className="space-y-4" data-testid="invoice-success">
            <div className="surface-muted p-4 space-y-2">
              <div className="text-xs text-muted-foreground">Fatura gerada</div>
              <div className="text-2xl font-display font-bold">{brl(invoice.total)}</div>
              <div className="text-xs text-muted-foreground">{invoice.count} serviço(s) · {invoice.billing_type}</div>
            </div>
            <div className="space-y-2">
              <Label>Link do relatório (compartilhe com o cliente)</Label>
              <div className="flex gap-2">
                <Input readOnly value={reportUrl} className="h-11 text-xs" data-testid="input-report-url" />
                <Button variant="outline" size="icon" onClick={copyReport} data-testid="btn-copy-report"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => window.open(reportUrl, "_blank")} data-testid="btn-view-report"><FileText className="h-4 w-4 mr-2" /> Ver relatório</Button>
              {invoice.invoice_url && <Button variant="outline" onClick={() => window.open(invoice.invoice_url, "_blank")} data-testid="btn-open-asaas"><ExternalLink className="h-4 w-4 mr-2" /> Abrir no Asaas</Button>}
              <Button className="col-span-2" onClick={shareWhats} data-testid="btn-share-invoice-whats"><MessageCircle className="h-4 w-4 mr-2" /> Enviar por WhatsApp</Button>
            </div>
          </div>
        ) : loading ? (
          <div className="py-12 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
        ) : appts.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">Nenhum serviço pendente de cobrança para este cliente.</div>
        ) : (
          <div className="space-y-4">
            <div className="surface-muted p-2 max-h-64 overflow-y-auto divide-y divide-border">
              <div className="p-2 flex items-center gap-2 sticky top-0 bg-muted/60">
                <Checkbox checked={selectedIds.length === appts.length} onCheckedChange={toggleAll} data-testid="check-all" />
                <span className="text-xs text-muted-foreground">Selecionar todos ({appts.length})</span>
              </div>
              {appts.map((a) => (
                <label key={a.id} className="p-2 flex items-center gap-3 text-sm cursor-pointer hover:bg-card/60 transition-colors" data-testid={`inv-appt-${a.id}`}>
                  <Checkbox checked={!!selected[a.id]} onCheckedChange={(v) => setSelected({ ...selected, [a.id]: !!v })} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{formatAppointmentServices(a)}</div>
                    <div className="text-xs text-muted-foreground">{fmtDateTime(a.scheduled_at)} · {a.professional_name}</div>
                  </div>
                  <div className="font-semibold">{brl(a.price)}</div>
                </label>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Forma de pagamento</Label>
                <Select value={billingType} onValueChange={setBillingType}>
                  <SelectTrigger data-testid="select-inv-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PIX">PIX</SelectItem>
                    <SelectItem value="BOLETO">Boleto</SelectItem>
                    <SelectItem value="CREDIT_CARD">Cartão de crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Vencimento</Label><Input data-testid="input-inv-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-10" /></div>
            </div>

            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="toggle-inv-nfe">
              <input
                type="checkbox"
                checked={issueInvoice}
                onChange={(e) => setIssueInvoice(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
              />
              <span>Emitir NF-e junto no Asaas <span className="block text-[10px]">(requer CEP/endereço no cliente e Município configurado em Integrações → Asaas)</span></span>
            </label>

            <div className="surface-muted p-4 space-y-3">
              <div className="text-sm font-medium">Ajustes de fechamento</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label className="text-xs">Tipo desconto</Label>
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger data-testid="select-discount-type" className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">R$ (valor)</SelectItem>
                      <SelectItem value="percentage">% (percentual)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Desconto</Label>
                  <Input data-testid="input-discount" type="number" min="0" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="h-10" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Acréscimo (R$)</Label>
                  <Input data-testid="input-surcharge" type="number" min="0" step="0.01" value={surcharge} onChange={(e) => setSurcharge(e.target.value)} className="h-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Observação da cobrança (opcional)</Label>
                <Textarea data-testid="input-desc-override" rows={2} value={descOverride} onChange={(e) => setDescOverride(e.target.value)} placeholder="Deixe em branco para usar a lista de serviços padrão" />
              </div>
            </div>

            <div className="surface-muted p-4 space-y-1">
              <div className="flex justify-between text-sm text-muted-foreground"><span>Subtotal</span><span>{brl(subtotal)}</span></div>
              {discountAmount > 0 && <div className="flex justify-between text-sm text-destructive"><span>Desconto</span><span>-{brl(discountAmount)}</span></div>}
              {surchargeAmount > 0 && <div className="flex justify-between text-sm"><span>Acréscimo</span><span>+{brl(surchargeAmount)}</span></div>}
              <div className="flex justify-between text-xl font-display font-bold pt-2 border-t border-border" data-testid="inv-total">
                <span>Total</span><span>{brl(total)}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!invoice && appts.length > 0 && (
            <Button data-testid="btn-generate-invoice" disabled={creating || total <= 0 || !can("payments.charge")} onClick={generate} className="h-11">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
              Gerar cobrança de {brl(total)}
            </Button>
          )}
          {invoice && <Button variant="outline" onClick={onClose}>Fechar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
